import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DefaultButton from '../Button/DefaultButton';
import DetailWrapper from '../CardRow/DetailWrapper';
import { HeadTitle } from '../CustomizedComponents/Texts';
import ButtonWrapper from '../Button/ButtonWrapper';
import CustomTextField from '../CustomizedComponents/CustomTextField';
import { useForm } from 'react-hook-form';
import useAppContext from '../../hooks/context/useAppContext';
import { useGetCustomerEnum } from '../../hooks/enums/useEnumHook';
import CustomSnackBar from '../CustomizedComponents/CustomSnackbar';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { supportedLanguages } from '../../localizations/index';
import Autocomplete from '@material-ui/lab/Autocomplete';
import userRoles from '../../constants/userRoles';

const show = (loaders) => {
  return loaders?.some((item) => item === true);
};

const UserDetail = ({ user, handlers }) => {
  const { handleSubmit, register, errors } = useForm();

  const {
    id,
    idSap,
    firstname,
    surname,
    email,
    phone,
    role,
    language,
    customers,
    putLoading,
    postLoading,
    putError,
    postError,
    postData,
    postDataState,
    postLoadingState,
    postErrorState,
    state,
    usersEnum,
    kamIds,
  } = user;

  const {
    setIdSap,
    setFirstname,
    setSurname,
    setEmail,
    setPhone,
    setRole,
    setLanguage,
    sendHandler,
    sendUserState,
    setKamIds,
    setCustomers,
  } = handlers;

  const { getEnums, getUser, setBackNavigation } = useAppContext();
  const { getCustomerEnum, data: customerData } = useGetCustomerEnum();

  const [customersEnum, setCustomersEnum] = useState([]);
  const { roles } = getEnums();
  const history = useHistory();
  const intl = useIntl();
  const success = intl.formatMessage({ id: 'app.action.saved' });
  const dataMissing = intl.formatMessage({ id: 'app.data.missing' });
  const dataIncorrect = intl.formatMessage({ id: 'app.data.incorrect' });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(success);
  const [issues, setIssues] = useState(null);
  const [editable, setEditable] = useState(false);
  const cancel = 'app.buttons.cancel';
  const save = 'app.buttons.save';
  const active = 'app.buttons.active';
  const inactive = 'app.buttons.inactive';
  const representativeTitle = 'app.users.kam';
  const customerTitle = 'app.users.customer';
  const location = useLocation();
  const loggedUser = getUser();

  const handlerUser = (user) => {
    setKamIds(user.map((item) => item.id));
  };

  useEffect(() => {
    if (putError) {
      setMessage(putError?.data?.message);
      setIssues(putError?.data?.issues);
    } else if (postError) {
      setMessage(postError?.data?.message);
      setIssues(postError?.data?.issues);
    } else {
      setMessage(success);
      setIssues(null);
    }
    postData && history.push(`/users/${postData.id}`);
  }, [putError, postError, success, history, postData]);

  useEffect(() => {
    loggedUser?.role === 'ADMIN' && setEditable(true);
  }, [getUser, loggedUser]);

  useEffect(() => {
    location.pathname === '/profile' ? setBackNavigation(null) : setBackNavigation(`/users`);
  }, [setBackNavigation, location]);

  useEffect(() => {
    (async () => {
      await getCustomerEnum();
    })();
  }, [getCustomerEnum]);

  useEffect(() => {
    if (customerData) {
      setCustomersEnum(customerData);
    }
  }, [customerData]);

  const handleChange = (idButton) => {
    if (idButton !== 'cancel') {
      setOpen(true);
      sendHandler();
    } else {
      location.pathname === '/profile' ? history.goBack() : history.push(`/users`);
    }
  };

  useEffect(() => {
    if (postDataState && !postErrorState && !postLoadingState) {
      setMessage(success);
      setOpen(true);
    }
  }, [postDataState, postErrorState, postLoadingState, success]);

  return (
    <>
      <DetailWrapper>
        <form onSubmit={handleSubmit(handleChange)}>
          <HeadTitle title={'app.user.personalInfo'} />
          <CustomTextField
            id="sapId"
            name="sapId"
            onChange={(event) => {
              setIdSap(event.target.value);
            }}
            title={'app.users.sapId'}
            value={idSap}
            variant="outlined"
            disabled={!editable}
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.sapId?.message}
            error={!!errors?.sapId}
          />
          <CustomTextField
            id="firstname"
            name="firstname"
            onChange={(event) => {
              setFirstname(event.target.value);
            }}
            title={'app.users.firstname'}
            value={firstname}
            variant="outlined"
            disabled={!editable}
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.firstname?.message}
            error={!!errors?.firstname}
          />
          <CustomTextField
            id="surname"
            name="surname"
            onChange={(event) => {
              setSurname(event.target.value);
            }}
            title={'app.users.surname'}
            value={surname}
            disabled={!editable}
            variant="outlined"
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.surname?.message}
            error={!!errors?.surname}
          />
          <CustomTextField
            id="email"
            name="email"
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            title={'app.users.email'}
            disabled={!editable}
            value={email}
            variant="outlined"
            inputRef={register({
              required: dataMissing,
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: dataIncorrect,
              },
            })}
            helperText={errors?.email?.message}
            error={!!errors?.email}
          />
          <CustomTextField
            id="phone"
            name="phone"
            onChange={(event) => {
              setPhone(event.target.value);
            }}
            title={'app.users.phone'}
            disabled={!editable}
            value={phone}
            variant="outlined"
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.phone?.message}
            error={!!errors?.phone}
          />

          <CustomTextField
            id="language"
            name="language"
            onChange={(event) => {
              setLanguage(event.target.value);
            }}
            select
            title={'app.users.communicationLanguage'}
            value={language}
            disabled={!editable}
            variant="outlined"
            SelectProps={{
              native: true,
            }}
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.language?.message}
            error={!!errors?.language}>
            {supportedLanguages.map((item) => (
              <option key={item.code} value={item.code}>
                {item.code}
              </option>
            ))}
          </CustomTextField>

          <CustomTextField
            id="role"
            name="role"
            onChange={(event) => {
              setRole(event.target.value);
            }}
            select
            disabled={!editable}
            title={'app.users.role'}
            value={role}
            SelectProps={{
              native: true,
            }}
            variant="outlined"
            inputRef={register({
              required: dataMissing,
            })}
            helperText={errors?.role?.message}
            error={!!errors?.role}>
            <option value={null} />
            {roles.map((item) => (
              <option key={item.roleCode} value={item.roleCode}>
                {item.roleCode}
              </option>
            ))}
          </CustomTextField>

          {
            user.role === userRoles.ASM &&
            <Autocomplete
              multiple
              id={'kamIds'}
              disabled={!editable}
              options={usersEnum?.filter((item) => item.role === userRoles.KAM)}
              onChange={(e, v) => handlerUser(v)}
              getOptionLabel={(user) => `${user.firstname} ${user.surname} (${user.email})`}
              value={usersEnum?.filter((user) => kamIds.indexOf(user.id) !== -1)}
              renderInput={(params) => <CustomTextField {...params} variant="outlined" title={representativeTitle} />}
            />
          }
          {
            (user.role === userRoles.CUSTOMER || user.role === userRoles.CUSTOMER_MULTIPLE_COTRACTS || user.role === userRoles.CUSTOMER_VIEWER ||
              user.role === userRoles.CUSTOMER_VIEWER_MULTIPLE_COTRACTS) &&
            <Autocomplete
              multiple
              id={'customerIds'}
              disabled={!editable}
              options={customersEnum}
              onChange={(e, v) => setCustomers(v) }
              getOptionLabel={(customer) => customer.name }
              value={customersEnum?.filter((customer) => customers?.findIndex((selectedCustomers) => selectedCustomers.id === customer.id) !== -1 )}
              renderInput={(params) => <CustomTextField {...params} variant="outlined" title={customerTitle} />}
            />
          }
          <ButtonWrapper>
            {loggedUser?.role === 'ADMIN' && (
              <DefaultButton
                id={'cancel'}
                primary={false}
                disabled={!editable}
                title={cancel}
                onClick={(event) => {
                  handleChange(event.currentTarget.id);
                }}
              />
            )}
            {loggedUser?.role === 'ADMIN' && id !== 'new' && (
              <DefaultButton
                onClick={sendUserState}
                primary={false}
                title={!postLoadingState && state === 1 ? inactive : active}
                loaders={[postLoadingState]}
              />
            )}
            {loggedUser?.role === 'ADMIN' && (
              <DefaultButton primary={true} disabled={!editable} type="submit" title={save} loaders={[putLoading, postLoading]} />
            )}
          </ButtonWrapper>
        </form>
        {!show([postLoading, putLoading]) && message && (
          <CustomSnackBar status={putError || postError ? 'error' : 'success'} message={message} setOpen={setOpen} open={open} />
        )}
        {!show([postLoading, putLoading]) &&
          issues &&
          issues.map((issue) => <CustomSnackBar key={issue} status={'error'} message={issue} setOpen={setOpen} open={open} />)}
      </DetailWrapper>
    </>
  );
};
UserDetail.propTypes = {
  user: PropTypes.object,
  handlers: PropTypes.object,
};

export default UserDetail;
